/**
 * Scene breaks (decisions 13, 35, 36). Every CommonMark thematic break (`***`, `* * *`, `---`, `___`, three or
 * more of one character with only spaces between) is a `SceneBreak` node, replacing Lezer's
 * `HorizontalRule`: a novel has no other use for a rule, and the stored source stays valid CommonMark
 * (decision 7). Lezer's own paragraph-interruption rule for thematic breaks stays in force, with its
 * precedence for setext headings (`Title` over `---`).
 */
import type { BlockContext, Line, MarkdownConfig } from '@lezer/markdown';
import { DASH, isSpace, STAR, UNDERSCORE } from './chars.ts';

/** Whether the line, from its first non-space character, is a thematic break (CommonMark 4.1). */
export function isSceneBreak(line: Line): boolean {
  const mark = line.next;
  if (mark !== STAR && mark !== DASH && mark !== UNDERSCORE) return false;
  let count = 0;
  for (let pos = line.pos; pos < line.text.length; pos += 1) {
    const code = line.text.charCodeAt(pos);
    if (code === mark) count += 1;
    else if (!isSpace(code)) return false;
  }
  return count >= 3;
}

/** The extension: `SceneBreak` takes `HorizontalRule`'s place, before lists, so `* * *` is never a list. */
export const sceneBreak: MarkdownConfig = {
  defineNodes: [{ name: 'SceneBreak', block: true }],
  parseBlock: [
    {
      name: 'SceneBreak',
      before: 'BulletList',
      parse(cx: BlockContext, line: Line): boolean {
        if (!isSceneBreak(line)) return false;
        const from = cx.lineStart + line.pos;
        cx.nextLine();
        cx.addElement(cx.elt('SceneBreak', from, cx.prevLineEnd()));
        return true;
      },
    },
  ],
};
